<template>
  <div class="datepicker-container">
    <input
      ref="dateInput"
      type="text"
      class="display-input"
      :placeholder="placeholderText"
      :value="formattedDates"
      readonly
    />
    <div v-if="!singleMode" class="overlay">
      <div class="overlay-button" @click="handleFromClick"></div>
      <div class="overlay-button" @click="handleToClick"></div>
    </div>
    <div v-else class="overlay-single">
      <div class="overlay-button-single" @click="handleSingleClick"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import Pikaday from "pikaday";
import "pikaday/css/pikaday.css";

const props = defineProps({
  modelValue: {
    type: [Array, Date],
    default: () => [],
  },
  singleMode: {
    type: Boolean,
    default: false,
  },
  // 📌 Nuevas props para fechas mínima y máxima
  minDate: {
    type: [Date, null],
    required: false,
    default: null,
  },
  maxDate: {
    type: [Date, null],
    required: false,
    default: null,
  },
  disabledDays: {
    type: Array,
    default: () => [],
    validator: (days) =>
      days.every((day) => typeof day === "number" && day >= 0 && day <= 6),
  },
  disabledDates: {
    type: Array,
    default: () => [],
    validator: (dates) => dates.every((date) => date instanceof Date),
  },
});

const emit = defineEmits(["update:modelValue"]);

const dateInput = ref(null);
let picker = null;

const fromDate = ref(null);
const toDate = ref(null);
const singleDate = ref(null);
const selectionTarget = ref(null);

const i18n = {
  previousMonth: "Mes anterior",
  nextMonth: "Mes siguiente",
  months: [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ],
  weekdays: [
    "Domingo",
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sábado",
  ],
  weekdaysShort: ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"],
};

const placeholderText = computed(() => {
  if (props.singleMode) {
    return singleDate.value ? formatDate(singleDate.value) : "Seleccioná fecha";
  }
  if (!fromDate.value && !toDate.value) {
    return "Desde / Hasta";
  }
  if (!fromDate.value && toDate.value) {
    return `Desde / ${formatDate(toDate.value)}`;
  }
  if (fromDate.value && !toDate.value) {
    return `${formatDate(fromDate.value)} / Hasta`;
  }
  return `${formatDate(fromDate.value)} / ${formatDate(toDate.value)}`;
});

const formattedDates = computed(() => {
  if (props.singleMode) {
    return singleDate.value ? formatDate(singleDate.value) : "";
  }
  if (!fromDate.value || !toDate.value) {
    return "";
  }
  return `${formatDate(fromDate.value)} / ${formatDate(toDate.value)}`;
});

function formatDate(date) {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}`;
}

// Función para verificar si un día debe ser deshabilitado
function isDateDisabled(date) {
  const day = date.getDay();
  // Verificar si es un día de la semana deshabilitado
  if (props.disabledDays.includes(day)) {
    return true;
  }
  // Verificar si es una fecha específica deshabilitada
  return props.disabledDates.some(
    (disabledDate) =>
      disabledDate.getFullYear() === date.getFullYear() &&
      disabledDate.getMonth() === date.getMonth() &&
      disabledDate.getDate() + 1 === date.getDate(),
  );
}

const handleFromClick = () => {
  selectionTarget.value = "from";
  // 📌 Se usa la prop minDate como límite inicial
  picker.setMinDate(props.minDate);
  // 📌 El límite máximo es la fecha "hasta" o la prop maxDate
  picker.setMaxDate(toDate.value || props.maxDate);
  picker.show();
};

const handleToClick = () => {
  selectionTarget.value = "to";
  // 📌 Se usa la fecha "desde" como límite mínimo
  picker.setMinDate(fromDate.value || props.minDate);
  // 📌 Se usa la prop maxDate como límite máximo
  picker.setMaxDate(props.maxDate);
  picker.show();
};

const handleSingleClick = () => {
  selectionTarget.value = "single";
  // 📌 Se usan las props minDate y maxDate para un solo modo
  picker.setMinDate(props.minDate);
  picker.setMaxDate(props.maxDate);
  picker.show();
};

onMounted(() => {
  // 📌 Inicialización de Pikaday con las nuevas props
  picker = new Pikaday({
    field: dateInput.value,
    maxDate: props.maxDate,
    minDate: props.minDate,
    i18n: i18n,
    autoClose: false,
    disableDayFn: isDateDisabled,
    onSelect: (date) => {
      if (props.singleMode) {
        singleDate.value = date;
        emit("update:modelValue", date);
        picker.hide();
      } else {
        if (selectionTarget.value === "from") {
          fromDate.value = date;
          if (
            toDate.value &&
            toDate.value.getTime() < fromDate.value.getTime()
          ) {
            toDate.value = null;
          }
        } else if (selectionTarget.value === "to") {
          toDate.value = date;
        }
        const finalDates = [fromDate.value, toDate.value];
        emit("update:modelValue", finalDates);

        if (fromDate.value && toDate.value) {
          picker.hide();
        }
      }
    },
    onOpen: () => {
      const pickerEl = document.querySelector(".pika-single");
      if (pickerEl) {
        let currentLeft = parseFloat(pickerEl.style.left) || 0;
        let currentTop = parseFloat(pickerEl.style.top) || 0;
        pickerEl.style.left = `${currentLeft + 5}px`;
        pickerEl.style.top = `${currentTop + 5}px`;
        pickerEl.style.paddingLeft = `3.12rem`;
        pickerEl.style.paddingRight = `3.12rem`;
      }
    },
  });

  if (props.singleMode && props.modelValue instanceof Date) {
    singleDate.value = props.modelValue;
  } else if (Array.isArray(props.modelValue) && props.modelValue.length > 0) {
    if (props.modelValue[0]) fromDate.value = props.modelValue[0];
    if (props.modelValue[1]) toDate.value = props.modelValue[1];
    picker.setMinDate(fromDate.value);
  }
});

onBeforeUnmount(() => {
  if (picker) {
    picker.destroy();
  }
});

watch(
  () => props.modelValue,
  (newDates) => {
    if (props.singleMode) {
      singleDate.value = newDates instanceof Date ? newDates : null;
    } else {
      if (!newDates || newDates.length === 0) {
        fromDate.value = null;
        toDate.value = null;
        if (picker) {
          // 📌 Se restablecen los límites a las props iniciales
          picker.setMinDate(props.minDate);
          picker.setMaxDate(props.maxDate);
          picker.gotoDate(new Date());
        }
      } else if (newDates.length > 0) {
        fromDate.value = newDates[0];
        toDate.value = newDates[1];
      }
    }
  },
  { deep: true },
);
</script>

<style scoped>
.datepicker-container {
  position: relative;
  width: 100%;
}
.datepicker-container .display-input {
  background: var(--backgroundPlaceholder);
  width: 100%;
  height: 6.678vh;
  padding: 10px;
  border-radius: 8px;
  border: none;
  outline: none;
  box-shadow: none;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;

  font: inherit; /* Hereda font-size, font-weight, font-family, line-height */
  color: inherit;
}
.datepicker-container .overlay {
  margin-left: 10px;
  position: absolute;
  top: 0;
  left: 0;
  width: 25%;
  height: 100%;
  display: flex;
}
.datepicker-container .overlay-button {
  flex: 1;
  cursor: pointer;
}
</style>

<style>
/* Estilos globales para la librería de Pikaday */
.pika-single {
  /* Sumar 5px al top y left 
  /* transform: translate(5px, 5px); */
  z-index: 100000;
}

/* Estos estilos son globales y afectarán a cualquier elemento .pika-table th en la página */
.pika-table th abbr {
  border-bottom: none;
  text-decoration: none;
  cursor: default;
}
.pika-table th {
  color: unset; /* Cambia el color del texto */
}
.pika-lendar {
  width: 17.76vw;
  max-width: 25.256vw;
  margin-left: 0rem;
  margin-right: 0rem;
}
.pika-button {
  text-align: center;
}

.pika-prev {
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNiIgaGVpZ2h0PSIxMSIgdmlld0JveD0iMCAwIDYgMTEiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik00LjI5NDUgOS41Mjg4OUwwLjg2OTE0MSA1LjMyMzA0TDQuMjk0NSAxLjExNzE5IiBzdHJva2U9IiMwMEMzQTgiIHN0cm9rZS13aWR0aD0iMS42MzExMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=);
  background-size: 6px 11px;
  opacity: 1;
}

.pika-next {
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNiIgaGVpZ2h0PSIxMSIgdmlld0JveD0iMCAwIDYgMTEiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik00LjI5NDUgOS41Mjg4OUwwLjg2OTE0MSA1LjMyMzA0TDQuMjk0NSAxLjExNzE5IiBzdHJva2U9IiMwMEMzQTgiIHN0cm9rZS13aWR0aD0iMS42MzExMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=);
  background-size: 6px 11px;
  opacity: 1;
  transform: rotate(180deg);
}

.pika-button {
  background: transparent;
}

.is-disabled .pika-button,
.is-inrange .pika-button {
  background: transparent;
}

.pika-button:hover {
  background: var(--color-success);
  opacity: 0.5;
}
.is-selected .pika-button {
  background: var(--color-success);
}
.is-today .pika-button {
  color: var(--color-success);
}

.is-today .pika-button:hover {
  color: var(--bg-popup);
}

.is-today.is-selected .pika-button {
  color: var(--bg-popup);
}
@media (max-width: 768px) {
  .pika-single {
    width: fit-content;
  }
  .pika-lendar {
    width: fit-content;
    max-width: fit-content;
  }
}
</style>
